if (!customElements.get('product-groups-carousel')) {
  class ProductGroupsCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-product-groups-viewport]');
      this.prevButton = this.querySelector('[data-product-groups-prev]');
      this.nextButton = this.querySelector('[data-product-groups-next]');
      this.track = this.viewport?.querySelector('.product-groups-carousel__track');
      this.activeIndex = 0;
      this.isRefreshing = false;

      if (!this.viewport || !this.prevButton || !this.nextButton || !this.track) return;

      this.prevButton.addEventListener('click', this.#onPrevClick);
      this.nextButton.addEventListener('click', this.#onNextClick);
      this.viewport.addEventListener('scroll', this.#onScroll, { passive: true });
      this.viewport.addEventListener('scrollend', this.#onScrollEnd);
      window.addEventListener('resize', this.#onResize);

      this.resizeObserver = new ResizeObserver(() => {
        if (this.isRefreshing) return;

        this.#refresh(false);
      });
      this.resizeObserver.observe(this);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#refresh(false));
      });
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.#onPrevClick);
      this.nextButton?.removeEventListener('click', this.#onNextClick);
      this.viewport?.removeEventListener('scroll', this.#onScroll);
      this.viewport?.removeEventListener('scrollend', this.#onScrollEnd);
      window.removeEventListener('resize', this.#onResize);
      this.resizeObserver?.disconnect();
    }

    #onPrevClick = () => {
      this.#scrollByPage(-1);
    };

    #onNextClick = () => {
      this.#scrollByPage(1);
    };

    #onScroll = () => {
      if (this.isRefreshing) return;

      this.#syncIndexFromScroll();
      this.#layout();
    };

    #onScrollEnd = () => {
      if (this.isRefreshing) return;

      this.#syncIndexFromScroll();
      this.#refresh(false);
    };

    #onResize = () => {
      this.#refresh(false);
    };

    #items() {
      return this.track.querySelectorAll('.product-option__label');
    }

    #getGap() {
      return parseFloat(window.getComputedStyle(this.track).columnGap || window.getComputedStyle(this.track).gap) || 0;
    }

    #getItemMetrics() {
      const firstItem = this.#items()[0];

      if (!firstItem) {
        return { itemWidth: 0, gap: 0, itemStride: 0 };
      }

      const gap = this.#getGap();
      const itemWidth = firstItem.getBoundingClientRect().width;

      return {
        itemWidth,
        gap,
        itemStride: itemWidth + gap
      };
    }

    #syncButtonSize(itemWidth) {
      for (const button of [this.prevButton, this.nextButton]) {
        button.style.width = `${itemWidth}px`;
        button.style.height = `${itemWidth}px`;
        button.style.flexBasis = `${itemWidth}px`;
      }
    }

    #getTotalSlots(itemStride, gap) {
      if (itemStride <= 0) return 1;

      return Math.max(1, Math.floor((this.clientWidth + gap) / itemStride));
    }

    #layout() {
      const totalCount = this.#items().length;
      const { itemWidth, gap, itemStride } = this.#getItemMetrics();

      if (itemStride <= 0 || totalCount === 0) {
        this.prevButton.hidden = true;
        this.nextButton.hidden = true;
        return { visibleCount: 0, maxIndex: 0, totalCount, itemStride };
      }

      this.#syncButtonSize(itemWidth);

      const totalSlots = this.#getTotalSlots(itemStride, gap);
      const showPrev = this.activeIndex > 0;

      // Prefer next when remaining items don't fit in the free slots
      let showNext = false;
      let visibleCount = totalSlots - (showPrev ? 1 : 0);

      if (this.activeIndex + visibleCount < totalCount) {
        showNext = true;
        visibleCount = Math.max(1, visibleCount - 1);
      }

      // Clamp index to last full page
      let maxIndex = Math.max(0, totalCount - visibleCount);

      if (this.activeIndex > maxIndex) {
        this.activeIndex = maxIndex;
      }

      // Re-evaluate after clamp — last page should never keep next
      const showPrevFinal = this.activeIndex > 0;

      visibleCount = totalSlots - (showPrevFinal ? 1 : 0);
      showNext = this.activeIndex + visibleCount < totalCount;

      if (showNext) {
        visibleCount = Math.max(1, visibleCount - 1);
      }

      maxIndex = Math.max(0, totalCount - visibleCount);

      if (this.activeIndex >= maxIndex) {
        this.activeIndex = maxIndex;
        showNext = false;
        visibleCount = Math.min(totalCount, totalSlots - (this.activeIndex > 0 ? 1 : 0));
        maxIndex = Math.max(0, totalCount - visibleCount);
        this.activeIndex = maxIndex;
      }

      const prevVisible = this.activeIndex > 0;

      this.prevButton.hidden = !prevVisible;
      this.nextButton.hidden = !showNext;

      // Exact width for N full swatches only — no clipped partial item
      const viewportWidth = visibleCount * itemWidth + Math.max(0, visibleCount - 1) * gap;

      this.viewport.style.flex = `0 0 ${viewportWidth}px`;
      this.viewport.style.width = `${viewportWidth}px`;
      this.viewport.style.maxWidth = `${viewportWidth}px`;

      this.classList.toggle('product-groups-carousel--has-overflow', maxIndex > 0);
      this.classList.toggle('product-groups-carousel--show-prev', prevVisible);
      this.classList.toggle('product-groups-carousel--show-next', showNext);

      return { visibleCount, maxIndex, totalCount, itemStride };
    }

    #scrollByPage(direction) {
      const { visibleCount, maxIndex } = this.#layout();

      this.activeIndex = Math.min(maxIndex, Math.max(0, this.activeIndex + direction * visibleCount));

      const layout = this.#layout();

      this.activeIndex = Math.min(layout.maxIndex, this.activeIndex);
      this.#layout();
      this.#scrollToIndex(this.activeIndex, true);
    }

    #scrollToIndex(index, smooth) {
      const { itemStride } = this.#getItemMetrics();

      this.viewport.scrollTo({
        left: index * itemStride,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }

    #syncIndexFromScroll() {
      const { itemStride } = this.#getItemMetrics();

      if (itemStride <= 0) return;

      const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);

      if (maxScrollLeft <= 1) {
        this.activeIndex = 0;
        return;
      }

      if (this.viewport.scrollLeft >= maxScrollLeft - 1) {
        const visibleCount = Math.max(1, Math.floor((this.viewport.clientWidth + this.#getGap()) / itemStride));

        this.activeIndex = Math.max(0, this.#items().length - visibleCount);
        return;
      }

      this.activeIndex = Math.max(0, Math.round(this.viewport.scrollLeft / itemStride));
    }

    #refresh(smooth) {
      this.isRefreshing = true;

      const { maxIndex } = this.#layout();

      this.activeIndex = Math.min(this.activeIndex, maxIndex);
      this.#layout();
      this.#scrollToIndex(this.activeIndex, smooth);

      requestAnimationFrame(() => {
        this.isRefreshing = false;
      });
    }
  }

  customElements.define('product-groups-carousel', ProductGroupsCarousel);
}
